import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Camera, Zap, Lightbulb, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import VideoHero from "@/components/VideoHero";

export default function Index() {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  return <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="relative px-6 pt-16 pb-20 sm:px-6 sm:pt-24 lg:pt-32 lg:pb-28">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute left-1/2 top-0 h-[500px] w-[1000px] -translate-x-1/2 bg-gradient-to-br from-primary/20 via-blue-50 to-background blur-3xl"></div>
          </div>
          
          <div className="relative mx-auto max-w-7xl">
            <div className="text-center">
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-balance transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                Transform everyday objects into 
                <span className="text-primary"> creative joy</span>
              </h1>
              
              <p className={`mt-3 sm:mt-5 mx-auto max-w-2xl text-xl text-muted-foreground transition-all duration-700 delay-100 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                Take a photo of objects around you, and get AI-powered ideas for creative activities to enjoy with your children.
              </p>
              
              <div className={`mt-8 sm:mt-10 transition-all duration-700 delay-150 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <VideoHero wistiaId="acf75ttnbe" className="mb-8" />
              </div>
              
              <div className={`mt-8 sm:mt-10 transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <Link to="/pricing">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 font-medium">
                    <Camera className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        {/* Features Section */}
        <section className="py-12 bg-white sm:py-16 lg:py-20">
          <div className="px-6 mx-auto max-w-7xl lg:px-8">
            <div className="max-w-2xl mx-auto lg:text-center">
              <h2 className="text-base font-semibold leading-7 text-primary">How It Works</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight font-display sm:text-4xl">
                Simple, creative, memorable
              </p>
              <p className="mt-4 text-lg text-muted-foreground">
                Turn screen time into creative time with personalized crafting ideas based on the objects around you.
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto mt-12 sm:mt-16 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-3">
                <FeatureCard icon={<Camera className="h-6 w-6 text-white" />} title="Capture Objects" description="Take a photo of everyday objects around your home using your device's camera." />
                
                <FeatureCard icon={<Zap className="h-6 w-6 text-white" />} title="AI-Powered Ideas" description="Our AI analyzes the image and generates unique creative activity ideas tailored to your objects." />
                
                <FeatureCard icon={<Lightbulb className="h-6 w-6 text-white" />} title="Create Together" description="Follow the simple instructions to create something special with your children." />
              </dl>
            </div>
          </div>
        </section>
        
        {/* Benefits Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-secondary/50">
          <div className="px-6 mx-auto max-w-7xl lg:px-8">
            <div className="max-w-2xl mx-auto lg:text-center">
              <h2 className="text-base font-semibold leading-7 text-primary">Benefits</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight font-display sm:text-4xl">
                Why parents love CraftyJoy
              </p>
              <p className="mt-4 text-lg text-muted-foreground">
                More than just craft ideas - a way to create meaningful moments with your children.
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto mt-12 sm:mt-16 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2">
                <BenefitCard title="Quality Time Together" description="Create meaningful moments that strengthen your bond with your children." />
                
                <BenefitCard title="Reduce Screen Time" description="Transform passive screen time into active, hands-on creative experiences." />
                
                <BenefitCard title="Endless Creative Ideas" description="Never run out of inspiration with personalized activity suggestions." />
                
                <BenefitCard title="Use What You Have" description="No special materials needed - use items you already have at home." />
              </dl>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-16 sm:py-24">
          <div className="px-6 mx-auto max-w-7xl lg:px-8">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold tracking-tight font-display sm:text-4xl">
                Ready to start creating?
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Take a photo and discover creative activities to enjoy with your children in minutes.
              </p>
              <div className="mt-10">
                <Link to="/pricing">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 font-medium">
                    <Camera className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-between lg:px-8">
          <div className="flex justify-center space-x-6 md:order-2">
            <p className="text-sm text-muted-foreground">© 2025 CraftyJoy. All rights reserved.</p>
          </div>
          <div className="mt-8 md:order-1 md:mt-0">
            <p className="text-center text-xs leading-5 text-muted-foreground">
              Made with love for parents and children everywhere.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}

function FeatureCard({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return <div className="relative group">
      <div className="absolute -inset-px bg-gradient-to-r from-primary to-blue-400 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <div className="relative glass-card rounded-2xl p-6 h-full">
        <dt className="flex items-center gap-x-3 text-lg font-semibold leading-7">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            {icon}
          </div>
          {title}
        </dt>
        <dd className="mt-4 text-base leading-7 text-muted-foreground">{description}</dd>
      </div>
    </div>;
}

function BenefitCard({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="flex gap-x-4">
      <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-primary bg-opacity-10">
        {title.includes("Time") ? <Clock className="h-6 w-6 text-primary" /> : <Sparkles className="h-6 w-6 text-primary" />}
      </div>
      <div>
        <dt className="text-lg font-semibold leading-7">{title}</dt>
        <dd className="mt-1 text-base leading-7 text-muted-foreground">{description}</dd>
      </div>
    </div>;
}
